import React, { useEffect, useState } from "react";
import { Flex, Typography, Button, Form, Input, Alert } from "antd";
import { UserAuth } from "../../../middleware/UserContext/UserContext.middleware";
import { NavLink, useNavigate } from "react-router-dom";
import { GoogleCircleFilled } from "@ant-design/icons";
import { createUser as apiCreateUser } from "../../../infrastructure/http/AuthAPI";
import { UserCredential, AuthError } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, CreateUser, loginWithGoogle, logout } = UserAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { t } = useTranslation();

  const emailValidationRules = [
    { required: true, message: t('email-required', { ns: 'validations' }) },
    { type: "email" as const, message: t('email-valid', { ns: 'validations' }) },
  ];
  
  const passwordValidationRules = [
    { required: true, message: t('password-required', { ns: 'validations' }) },
    { min: 6, message: t('password-min-length', { ns: 'validations' }) },
  ];

  const onSignUp = (values: { email: string; password: string }) => {
    setLoading(true);
    setErrorMessage(null);
    CreateUser(values.email, values.password)
      .then((uc: UserCredential) => {
        return apiCreateUser(uc);
      })
      .catch((error: AuthError) => {
        setErrorMessage(t(error.code, { ns: 'errors' }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClickGoogleLogin = async () => {
    setGoogleLoading(true);
    setErrorMessage(null);
    loginWithGoogle()
      .then((uc: UserCredential) => {
        return apiCreateUser(uc);
      })
      .catch((error: AuthError) => {
        setErrorMessage(t(error.code, { ns: 'errors' }));
        logout();
      })
      .finally(() => {
        setGoogleLoading(false);
      });
  };

  useEffect(() => {
    if (currentUser) {
      navigate("/");
    }
  }, [navigate, currentUser]);

  return (
    <Flex vertical align="center" justify="center">
      <Typography.Title level={3}>
        {t('signup-title', { ns: 'auth' })}
      </Typography.Title>
      {errorMessage && (
        <Alert 
          message={errorMessage} 
          type="error" 
          showIcon 
          style={{ marginBottom: '16px', width: '100%', maxWidth: '400px' }} 
        />
      )}
      <Form
        name="sign_up_form"
        initialValues={{ remember: true }}
        autoComplete="off"
        onFinish={onSignUp}
        style={{ width: '100%', maxWidth: '400px' }}
        layout='vertical'
      >
        <Form.Item label={t('email', { ns: 'auth' })} name="email" rules={emailValidationRules}>
          <Input placeholder={t('email-placeholder', { ns: 'auth' })} disabled={loading || googleLoading} />
        </Form.Item>

        <Form.Item
          label={t('password', { ns: 'auth' })}
          name="password"
          rules={passwordValidationRules}
        >
          <Input.Password placeholder={t('password-placeholder', { ns: 'auth' })} disabled={loading || googleLoading} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading} disabled={googleLoading}>
            {t('signup-button', { ns: 'auth' })}
          </Button>
          <Button
            onClick={onClickGoogleLogin}
            icon={<GoogleCircleFilled />}
            block 
            style={{ marginTop: '16px' }} 
            loading={googleLoading}
            disabled={loading}
          >
            {t('signup-with-google', { ns: 'auth' })}
          </Button>
        </Form.Item>
      </Form>
      <Typography.Paragraph style={{ textAlign: 'center', marginTop: '16px' }}>
        {t('already-registered', { ns: 'auth' })}{' '}
        <NavLink to="/login">{t('login-link', { ns: 'auth' })}</NavLink>
      </Typography.Paragraph>
    </Flex>
  );
};

export default SignUpPage;
